import axios from "../utils/axios";
import { createAsyncThunk } from "@reduxjs/toolkit";

export const getCategories = createAsyncThunk(
  "categories",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      let url = "category";
      if (selectedMerchant) {
        url += `?merchantId=${selectedMerchant._id}`;
      }
      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      throw error;
    }
  }
);
export const getAllCategories = createAsyncThunk(
  "categories/all",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      let url = "category/getAll";
      if (selectedMerchant) {
        url += `?merchantId=${selectedMerchant._id}`;
      }
      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      throw error;
    }
  }
);

// Create a merchant-scoped category
export const createCategory = createAsyncThunk(
  "category/create",
  async (payload, thunkAPI) => {
    try {
      // Use the same endpoint as before but without merchantId requirement
      const response = await axios.post("category/create", payload);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);
export const createOffer = createAsyncThunk(
  "offer/create",
  async ({ offerId, ...values }, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    if (selectedMerchant && !values.merchantId) {
      values.merchantId = selectedMerchant._id;
    }
    const targetMerchantId = values.merchantId || selectedMerchant?._id;
    const merchantIdParam = targetMerchantId ? `?merchantId=${targetMerchantId}` : "";
    try {
      const endpoint = offerId
        ? `offer/create/${offerId}${merchantIdParam}`
        : `offer/create${merchantIdParam}`;
      const response = await axios.patch(endpoint, values);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getOffers = createAsyncThunk(
  "offer",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      const queryParams = new URLSearchParams();
      if (payload?.page) queryParams.append("page", payload.page);
      if (payload?.limit) queryParams.append("limit", payload.limit);
      if (payload?.search) queryParams.append("search", payload.search);
      if (payload?.category) queryParams.append("category", payload.category);
      if (payload?.subcategory) queryParams.append("subCategory", payload.subcategory);
      if (payload?.offerType) queryParams.append("offerType", payload.offerType);
      if (payload?.status) queryParams.append("status", payload.status);
      if (payload?.duration) queryParams.append("duration", payload.duration);
      if (payload?.redemptionMethod) queryParams.append("redemptionMethod", payload.redemptionMethod);
      if (payload?.creatorRole) queryParams.append("creatorRole", payload.creatorRole);
      // Revert to "offer/nonDrafted" (safe fallback) or keep it if necessary.
      // Since "offer/merchant" causes 403 for Admin, we cannot use it as a generic "get all".
      // We point this back to "nonDrafted" so it mirrors getNonDraftedOffers logic for safety.
      if (selectedMerchant) queryParams.append("merchantId", selectedMerchant._id);

      const queryString = queryParams.toString();
      const url = `offer/nonDrafted${queryString ? `?${queryString}` : ""}`;
      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const editOffer = createAsyncThunk(
  "offer/update",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      const id = payload.get("id");
      let url = `offer/${id}`;
      if (selectedMerchant) {
        url += `?merchantId=${selectedMerchant._id}`;
      }
      const response = await axios.patch(url, payload);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const deleteOffer = createAsyncThunk(
  "offer/delete",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    const createdOffer = state.offerReducer?.createdOffer;
    const offerUrl = `offer/${payload?.id}`;
    try {

      // Resolve merchantId from safest-to-most-generic sources
      // 1) explicit caller payload
      // 2) currently selected merchant (merchant role)
      // 3) merchant attached to the created draft (create flow cleanup)
      const targetMerchantId =
        payload?.merchantId ||
        selectedMerchant?._id ||
        createdOffer?.merchant?._id ||
        createdOffer?.merchantId ||
        null;

      const buildUrl = (mId) => {
        if (!mId) return offerUrl; // omit merchantId entirely
        const qp = new URLSearchParams();
        qp.set("merchantId", mId);
        return `${offerUrl}?${qp.toString()}`;
      };

      let url = buildUrl(targetMerchantId);

      const response = await axios.delete(url);
      const responseData = response?.data;
      if (responseData?.success === true) {
        return responseData;
      }

      // For any other non-success payload coming back as a value, reject so reducers handle it (normalize to object)
      if (typeof responseData === 'string') {
        return thunkAPI?.rejectWithValue({ message: responseData });
      }
      return thunkAPI?.rejectWithValue(responseData || { message: "Failed to delete the offer" });
    } catch (error) {
      // Fallback: If backend says merchant not found, retry without merchantId
      try {
        const msg = error?.response?.data?.message || "";
        if (/merchant not found/i.test(msg)) {
          const retry = await axios.delete(offerUrl);
          if (retry?.data?.success === true) return retry?.data;
          return thunkAPI?.rejectWithValue(retry?.data || { message: msg });
        }
      } catch (_) { }
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const toggleOfferStatus = createAsyncThunk(
  "offer/status",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.patch(`offer/status/${payload?.id}`, {
        status: payload?.status
      });
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const createSubCategory = createAsyncThunk(
  "subCategory/create",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      let url = "subCategory/create";
      if (selectedMerchant) {
        url += `?merchantId=${selectedMerchant._id}`;
      }
      const response = await axios.post(url, payload);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const deleteSubCategory = createAsyncThunk(
  "subCategory/delete",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;

    let id, merchantId;
    if (typeof payload === 'object' && payload !== null) {
      id = payload.id;
      merchantId = payload.merchantId;
    } else {
      id = payload;
    }

    try {
      let url = `subCategory/${id}`;
      const targetMerchantId = merchantId || selectedMerchant?._id;
      if (targetMerchantId) {
        url += `?merchantId=${targetMerchantId}`;
      }
      const response = await axios.delete(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const createRule = createAsyncThunk(
  "ruleAndRegulation",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      let url = "ruleAndRegulation";
      if (payload?.offerType) {
        url += `/${payload.offerType}`;
      }
      const queryParams = new URLSearchParams();
      if (selectedMerchant) {
        queryParams.append("merchantId", selectedMerchant._id);
        queryParams.append("merchantid", selectedMerchant._id);
      }
      if (Array.from(queryParams).length > 0) {
        const qp = new URLSearchParams(queryParams);
        const mId = qp.get("merchantId");
        if (mId && !qp.get("merchantid")) qp.append("merchantid", mId);
        url += `?${qp.toString()}`;
      }
      const response = await axios.post(url, payload);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getGroupedRules = createAsyncThunk(
  "ruleAndRegulation/grouped",
  async (payload, thunkAPI) => {
    try {
      const offerType = payload?.offerType;
      const url = offerType
        ? `ruleAndRegulation/grouped/${offerType}`
        : `ruleAndRegulation/grouped`;
      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const deleteRule = createAsyncThunk(
  "ruleAndRegulation/delete",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;

    let id, merchantId;
    if (typeof payload === 'object' && payload !== null) {
      id = payload.id;
      merchantId = payload.merchantId;
    } else {
      id = payload;
    }

    try {
      let url = `ruleAndRegulation/${id}`;
      const targetMerchantId = merchantId || selectedMerchant?._id;
      if (targetMerchantId) {
        url += `?merchantId=${targetMerchantId}`;
      }
      const response = await axios.delete(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getRules = createAsyncThunk(
  "get/ruleAndRegulation",
  async (payload = {}, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      const queryParams = new URLSearchParams();
      const targetMerchantId = payload?.merchantId || selectedMerchant?._id;
      if (targetMerchantId) {
        queryParams.append("merchantId", targetMerchantId);
      }

      let url = "ruleAndRegulation";
      if (payload?.offerType) {
        url += `/${payload.offerType}`;
      }

      // Ensure we send both casing versions to satisfy strict backend checks
      if (targetMerchantId && !queryParams.has("merchantid")) {
        queryParams.append("merchantid", targetMerchantId);
      }

      if (Array.from(queryParams).length > 0) {
        url += `?${queryParams.toString()}`;
      }

      try { console.log("getRules → request", { url, payload, targetMerchantId }); } catch { }
      const response = await axios.get(url);
      try { console.log("getRules → response", response?.data); } catch { }
      return response?.data;
    } catch (error) {
      try {
        const msg = error?.response?.data?.message || "";
        const status = error?.response?.status;
        const canRetryWithMerchant =
          status === 400 && /merchant id is required/i.test(msg);
        const fallbackMerchantId = payload?.merchantId || selectedMerchant?._id;
        if (canRetryWithMerchant && fallbackMerchantId) {
          const qp = new URLSearchParams();
          qp.append("merchantId", fallbackMerchantId);
          let retryUrl = "ruleAndRegulation";
          if (payload?.offerType) retryUrl += `/${payload.offerType}`;
          retryUrl += `?${qp.toString()}`;
          try { console.warn("getRules → retry with merchantId", { retryUrl, fallbackMerchantId }); } catch { }
          const retryRes = await axios.get(retryUrl);
          return retryRes?.data;
        }
      } catch (_) { }
      throw error;
    }
  }
);

export const getOfferById = createAsyncThunk(
  "offer/getById",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      let url = `offer/${payload.id}`;
      if (selectedMerchant) {
        url += `?merchantId=${selectedMerchant._id}`;
      }
      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

// New Offer Code Management Functions
export const getOfferRedemptionCodeCounts = createAsyncThunk(
  "offer/getRedemptionCodeCounts",
  async ({ offerId, merchantId }, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;

    try {
      // Use provided merchantId or fall back to selectedMerchant for RBAC
      const targetMerchantId = merchantId || selectedMerchant?._id;

      if (!targetMerchantId) {
        throw new Error("Merchant ID is required for offer code access");
      }

      const url = `offer/redemption-code-counts/${offerId}/${targetMerchantId}`;
      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const updateOfferCodes = createAsyncThunk(
  "offer/updateOfferCodes",
  async ({ offerId, offerCodes, merchantId }, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;

    try {
      // Use provided merchantId or fall back to selectedMerchant
      const targetMerchantId = merchantId || selectedMerchant?._id;

      console.log("updateOfferCodes - Input params:", { offerId, offerCodes, merchantId });
      console.log("updateOfferCodes - targetMerchantId:", targetMerchantId);
      console.log("updateOfferCodes - selectedMerchant from state:", selectedMerchant);

      if (!targetMerchantId) {
        throw new Error("Merchant ID is required for offer code updates");
      }

      if (!offerId) {
        throw new Error("Offer ID is required for offer code updates");
      }

      if (!offerCodes || offerCodes.length === 0) {
        throw new Error("At least one code is required");
      }

      // Validate that all codes are strings and not empty
      const validCodes = offerCodes.filter(code => typeof code === 'string' && code.trim().length > 0);
      if (validCodes.length === 0) {
        throw new Error("All codes must be non-empty strings");
      }

      // Additional validation for code format
      const processedCodes = validCodes.map(code => {
        const processed = code.trim().toUpperCase();
        if (processed.length < 4 || processed.length > 20) {
          throw new Error(`Code "${code}" must be between 4-20 characters`);
        }
        if (!/^[A-Z0-9]+$/.test(processed)) {
          throw new Error(`Code "${code}" contains invalid characters (only A-Z and 0-9 allowed)`);
        }
        return processed;
      });

      // Remove duplicates
      const uniqueCodes = [...new Set(processedCodes)];
      if (uniqueCodes.length !== processedCodes.length) {
        console.warn("updateOfferCodes - Duplicate codes were removed");
      }

      console.log("updateOfferCodes - Validated codes:", uniqueCodes);

      // Use the correct endpoint for adding redemption codes
      const url = `offer/create/${offerId}`;
      const payload = {
        offerCodes: uniqueCodes,
        merchantId: targetMerchantId
      };

      console.log("updateOfferCodes - Making request to:", url);
      console.log("updateOfferCodes - Payload:", payload);

      let response;
      try {
        response = await axios.patch(url, payload);
        console.log("updateOfferCodes - Response:", response?.data);
      } catch (firstError) {
        console.error("updateOfferCodes - First attempt failed:", firstError);

        // Try alternative endpoint if first fails
        const alternativeUrl = `offer/redemption-codes/${offerId}`;
        const alternativePayload = {
          offerCodes: uniqueCodes,
          merchantId: targetMerchantId
        };

        console.log("updateOfferCodes - Trying alternative endpoint:", alternativeUrl);
        console.log("updateOfferCodes - Alternative payload:", alternativePayload);

        response = await axios.post(alternativeUrl, alternativePayload);
        console.log("updateOfferCodes - Alternative response:", response?.data);
      }

      return response?.data;
    } catch (error) {
      console.error("updateOfferCodes - Error:", error);
      console.error("updateOfferCodes - Error response:", error?.response);

      // Provide better error handling
      let errorMessage = "Failed to add codes";

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // Log more detailed error information
      console.error("updateOfferCodes - Error status:", error?.response?.status);
      console.error("updateOfferCodes - Error data:", error?.response?.data);

      return thunkAPI?.rejectWithValue({
        message: errorMessage,
        status: error?.response?.status
      });
    }
  }
);

export const getOfferCodeDetails = createAsyncThunk(
  "offer/getOfferCodeDetails",
  async ({ offerId, merchantId, page = 1, limit = 10, status }, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;

    try {
      // Use provided merchantId or fall back to selectedMerchant for RBAC
      const targetMerchantId = merchantId || selectedMerchant?._id;

      if (!targetMerchantId) {
        throw new Error("Merchant ID is required for offer code access");
      }

      const queryParams = new URLSearchParams();
      queryParams.append("page", page);
      queryParams.append("limit", limit);
      if (status) queryParams.append("status", status);

      const url = `offer/redemption-code-counts/${offerId}/${targetMerchantId}?${queryParams.toString()}`;
      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getNearestOffersByMerchant = createAsyncThunk(
  "offer/nearby/merchant",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      const url = selectedMerchant
        ? `offer/nearby/merchant?merchantId=${selectedMerchant._id}`
        : "offer/nearby/merchant";
      const response = await axios.post(url, payload);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getCategoryById = createAsyncThunk(
  "category",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.get(`category/${payload?.id}`);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getOffersByMerchant = createAsyncThunk(
  "offer/merchant",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      const queryParams = new URLSearchParams();

      // Add all available parameters to the query string
      if (payload?.page) queryParams.append("page", payload.page);
      if (payload?.limit) queryParams.append("limit", payload.limit);
      if (payload?.search) queryParams.append("search", payload.search);
      if (payload?.category) queryParams.append("category", payload.category);
      if (payload?.subcategory)
        queryParams.append("subCategory", payload.subcategory);
      if (payload?.offerType)
        queryParams.append("offerType", payload.offerType);
      if (payload?.status) queryParams.append("status", payload.status);
      if (payload?.duration) queryParams.append("duration", payload.duration);
      if (payload?.redemptionMethod)
        queryParams.append("redemptionMethod", payload.redemptionMethod);
      if (payload?.startDate) queryParams.append("startDate", payload.startDate);
      if (payload?.endDate) queryParams.append("endDate", payload.endDate);
      if (payload?.sortBy) queryParams.append("sortBy", payload.sortBy);
      if (payload?.sort) queryParams.append("sort", payload.sort);
      if (payload?.order) queryParams.append("order", payload.order);
      const targetMerchantId = payload?.merchantId || selectedMerchant?._id;
      if (targetMerchantId)
        queryParams.append("merchantId", targetMerchantId);
      const queryString = queryParams.toString();
      const url = `offer/merchant?${queryString}`;
      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const approveOrRejectResources = createAsyncThunk(
  "admin/approveReject",
  async (payload, thunkAPI) => {
    try {
      const body = {
        ids: payload?.ids,
        merchantId: payload?.merchantId,
        reason: payload?.reason,
        // Optional email bodies for status changes where backend requires them
        ...(payload?.TextBody ? { TextBody: payload.TextBody } : {}),
        ...(payload?.HtmlBody ? { HtmlBody: payload.HtmlBody } : {}),
        // Some backends expect lowercase/camelCase variants; include if provided
        ...(payload?.textBody ? { textBody: payload.textBody } : {}),
        ...(payload?.htmlBody ? { htmlBody: payload.htmlBody } : {}),
      };
      const url = `admin/approveReject?type=${payload?.type}&status=${payload?.status}`;
      // Debug logs for visibility in browser console
      try {
        console.log("approveOrRejectResources → request", {
          url,
          query: { type: payload?.type, status: payload?.status },
          body,
        });
      } catch { }
      const response = await axios.post(url, body);
      let data = response?.data;
      try {
        console.log("approveOrRejectResources → response", data);
      } catch { }
      // Backend sometimes returns 200 with success:false and the same
      // email body message even though the status is updated. Normalize
      // this case to a soft success to avoid user-facing error toasts.
      const respMsg = data?.message || "";
      const isEmailBodyWarning200 =
        data?.success === false && /Provide either.*TextBody.*HtmlBody/i.test(respMsg);
      if (isEmailBodyWarning200) {
        data = { success: true, data: data?.data ?? null, message: "Status updated successfully." };
      }
      return data;
    } catch (error) {
      try {
        console.error("approveOrRejectResources → error", {
          message: error?.message,
          data: error?.response?.data ?? error?.data,
          status: error?.response?.status ?? error?.status,
        });
      } catch { }
      // Temporary backend quirk: API returns 422 with message
      // "Provide either 'TextBody' or 'HtmlBody' (or both)."
      // even though the status change succeeds. Treat this
      // specific case as a soft success to avoid user-facing errors.
      const normalizedStatus = error?.response?.status ?? error?.status;
      const normalizedMessage = error?.response?.data?.message ?? error?.data?.message ?? "";
      const isEmailBodyWarning =
        normalizedStatus === 422 && /Provide either.*TextBody.*HtmlBody/i.test(normalizedMessage);

      if (isEmailBodyWarning) {
        const softSuccess = {
          success: true,
          data: null,
          message: "Status updated successfully.",
        };
        try {
          console.warn("approveOrRejectResources → soft success despite 422", normalizedMessage);
        } catch { }
        return thunkAPI.fulfillWithValue(softSuccess);
      }

      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getNonDraftedOffers = createAsyncThunk(
  "offer/nonDrafted",
  async (payload, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams();

      // Add all available parameters to the query string
      if (payload?.page) queryParams.append("page", payload.page);
      if (payload?.limit) queryParams.append("limit", payload.limit);
      if (payload?.search) queryParams.append("search", payload.search);
      if (payload?.category) queryParams.append("category", payload.category);
      if (payload?.subcategory)
        queryParams.append("subCategory", payload.subcategory);
      if (payload?.offerType)
        queryParams.append("offerType", payload.offerType);
      if (payload?.status) queryParams.append("status", payload.status);
      if (payload?.duration) queryParams.append("duration", payload.duration);
      if (payload?.redemptionMethod)
        queryParams.append("redemptionMethod", payload.redemptionMethod);
      if (payload?.creatorRole) queryParams.append("creatorRole", payload.creatorRole);
      if (payload?.startDate) queryParams.append("startDate", payload.startDate);
      if (payload?.endDate) queryParams.append("endDate", payload.endDate);
      if (payload?.sortBy) queryParams.append("sortBy", payload.sortBy);
      if (payload?.sort) queryParams.append("sort", payload.sort);
      if (payload?.order) queryParams.append("order", payload.order);
      const targetMerchantId = payload?.merchantId || payload?.merchantid;
      if (targetMerchantId) queryParams.append("merchantId", targetMerchantId);
      const queryString = queryParams.toString();
      const url = `offer/nonDrafted?${queryString}`;
      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

// add offer codes api service
export const addOfferCodes = createAsyncThunk(
  "offer/addCodes",
  async ({ offerId, offerCodes, merchantId }, thunkAPI) => {
    try {
      console.log("addOfferCodes service - offerId:", offerId);
      console.log("addOfferCodes service - offerCodes:", offerCodes);
      console.log("addOfferCodes service - merchantId:", merchantId);

      const payload = {
        offerCodes: offerCodes,
        merchantId: merchantId
      };

      console.log("addOfferCodes service - payload:", payload);

      const response = await axios.patch(`offer/redemption-codes/${offerId}`, payload);
      console.log("addOfferCodes service - response:", response?.data);

      return response?.data;
    } catch (error) {
      console.error("addOfferCodes service - error:", error);
      console.error("addOfferCodes service - error response:", error?.response?.data);
      return thunkAPI?.rejectWithValue(error?.response?.data || error);
    }
  }
);

// update offer rank api service
export const updateOfferRank = createAsyncThunk(
  "offer/updateRank",
  async ({ offerId, ...values }, thunkAPI) => {
    try {
      const response = await axios.patch(`offer/ranking/${offerId}`, values);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

// send offer feedback api service
export const sendOfferFeedback = createAsyncThunk(
  "offer/sendFeedback",
  async ({ offerId, feedback, merchantId }, thunkAPI) => {
    try {
      const bodies = [
        { method: "put", url: `offer/${offerId}/feedback` },
        { method: "put", url: `offer/feedback/${offerId}` },
        { method: "post", url: `offer/${offerId}/feedback` },
        { method: "post", url: `offer/feedback/${offerId}` },
      ];

      let lastError;
      for (const attempt of bodies) {
        try {
          const response = await axios[attempt.method](attempt.url, { feedback });
          return response?.data;
        } catch (err) {
          lastError = err;
        }
      }
      throw lastError || new Error("Feedback route not found");
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const rearrangeHomeFilters = createAsyncThunk(
  "homeFilter/rearrange",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.patch("homeFilter/rearrange", payload);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getHomeFilters = createAsyncThunk(
  "homeFilter/list",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(`homeFilter?_=${Date.now()}`);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const addHomeFilter = createAsyncThunk(
  "homeFilter/add",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.post("homeFilter", payload);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const deleteHomeFilter = createAsyncThunk(
  "homeFilter/delete",
  async (id, thunkAPI) => {
    try {
      const response = await axios.delete(`homeFilter/${id}`);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);
